/**
 * @module EvidenxSpinner
 * @purpose Reusable busy indicator: a static inline-SVG key mark inside a
 *   spinning accent-coloured ring, revealed after a delay so a request that
 *   settles quickly never flashes the indicator on screen.
 * @depends none -- the mark is drawn inline, no icon library or asset file
 * @dependedOnBy pages/case/analysis/reasoning (header trigger, running state)
 *
 * @rules
 * - Theming: colour tokens (`--spinner`) resolve only under a theming
 *   ancestor (`.dashboard` / `.landing-page`, see styles/dashboard.css). Outside
 *   one the ring falls back to `currentColor` via the CSS `var(..., fallback)`
 *   form, so the component never renders with no visible colour at all.
 * - Coexistence: `EvidenxSpinner` indicates request-settlement/run-activity
 *   busy state on the control that fired it. `OperationProgress`
 *   (components/common/operation-progress) remains the facility for anything
 *   backed by a database `operation_progress` row -- reasoning gains one only
 *   with S8. Existing `Loader2` spinner sites stay as-is until a platform-wide
 *   sweep; this component does not replace them.
 * - Performance: the reveal-after-delay behaviour is pure CSS (the
 *   `evidenx-reveal` keyframe in styles/index.css) -- no JS timer, no
 *   re-render on reveal.
 */

type EvidenxSpinnerSize = 'inline' | 'block'

export interface EvidenxSpinnerProps {
  size?: EvidenxSpinnerSize
  label: string
  delayMs?: number
}

const DEFAULT_SIZE: EvidenxSpinnerSize = 'inline'
const DEFAULT_DELAY_MS = 1000
const REVEAL_DURATION = '150ms'
const REVEAL_EASING = 'ease-out'

const DIMENSION_CLASSES: Record<EvidenxSpinnerSize, string> = {
  inline: 'h-5 w-5',
  block: 'h-24 w-24',
}

const RING_BORDER_CLASSES: Record<EvidenxSpinnerSize, string> = {
  inline: 'border-2',
  block: 'border-8',
}

const KEY_CLASSES: Record<EvidenxSpinnerSize, string> = {
  inline: 'h-3 w-3',
  block: 'h-12 w-12',
}

/**
 * @behaviour The static key silhouette drawn as a stroke-only path so it
 *   tints with `currentColor` in either theme and stays legible at 16px.
 * @errors n/a -- purely decorative, hidden from assistive tech
 */
function KeyMark({ size }: { size: EvidenxSpinnerSize }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={KEY_CLASSES[size]}
    >
      <circle cx="8" cy="8" r="4" />
      <line x1="11.5" y1="11.5" x2="20" y2="20" />
      <line x1="16.5" y1="16.5" x2="19.5" y2="13.5" />
      <line x1="18.5" y1="18.5" x2="21.5" y2="15.5" />
    </svg>
  )
}

/**
 * Brand-evocative busy indicator: a static inline key mark inside a spinning
 * accent-coloured ring.
 *
 * @behaviour Reveals itself via the pure-CSS `evidenx-reveal` keyframe after
 *   `delayMs` (default 1000ms), so work that finishes before the delay never
 *   flashes the indicator. The ring respects `prefers-reduced-motion` via
 *   `motion-reduce:animate-none`. `role="status"` names the element with
 *   `label`; the root `data-testid` is size-scoped so both instances can
 *   render simultaneously without ambiguity.
 * @errors n/a -- purely presentational
 */
export function EvidenxSpinner({
  size = DEFAULT_SIZE,
  label,
  delayMs = DEFAULT_DELAY_MS,
}: EvidenxSpinnerProps): React.ReactElement {
  return (
    <div
      role="status"
      aria-label={label}
      data-testid={`evidenx-spinner-${size}`}
      className={`relative inline-flex items-center justify-center ${DIMENSION_CLASSES[size]}`}
      style={{
        animationName: 'evidenx-reveal',
        animationDuration: REVEAL_DURATION,
        animationTimingFunction: REVEAL_EASING,
        animationDelay: `${delayMs.toString()}ms`,
        animationFillMode: 'both',
      }}
    >
      <div
        className={`absolute inset-0 rounded-full animate-spin motion-reduce:animate-none ${RING_BORDER_CLASSES[size]}`}
        style={{ borderColor: 'var(--spinner, currentColor)', borderTopColor: 'transparent' }}
      />
      <KeyMark size={size} />
    </div>
  )
}
